import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { di } from '../../../../../../state/inversify.config';
import { AssessmentCreationViewService } from '../../../assessmentCreationView/assessmentCreation.view.service';
import { ModulesSpecsDomain } from '../modules.domain';
import { Notes } from '../../components/notes/notes.dumb';
import { ImmediateMemoryWordsList } from '../../components/wordsList/immediateMemoryWordsList.dumb';
import { Instructions } from '../../components/instructions/instructions.dumb';
import {
  getRandomWordsList,
  wordsPresentedFromKeyValList,
} from './immediateMemory.helper';
import { SkipModule } from '../../components/skipModule/skipModule.smart';
import { AssessmentModules } from '../../modules';
import { useCustomTranslation } from '../../../../../../translation/customTranslation';

export function ImmediateMemory({ domainService }: any) {
  const moduleSpecsDomain: ModulesSpecsDomain =
    di.get<ModulesSpecsDomain>(ModulesSpecsDomain);
  const { t } = useCustomTranslation('assessment');
  const assessmentCreationViewService: AssessmentCreationViewService =
    di.get<AssessmentCreationViewService>(AssessmentCreationViewService);
  const module = assessmentCreationViewService.useModuleSelector();

  const { immediateMemory } = module;
  const moduleName: string = 'immediateMemory';

  const { heading, instructions, data, note, responseList } =
    moduleSpecsDomain.immediateMemoryExamConfig();

  const [randomWordsList] = useState(
    immediateMemory?.wordsPresentedKeyVal
      ? immediateMemory?.wordsPresentedKeyVal
      : getRandomWordsList(data),
  );

  const saveModule = (): void => {
    assessmentCreationViewService.dispatchSetAssessmentModule({
      immediateMemory: {
        wordsPresentedKeyVal: randomWordsList,
        wordsPresented: wordsPresentedFromKeyValList(
          immediateMemory?.wordsPresentedKeyVal,
        ),
        immediateMemoryTrial1Response:
          immediateMemory?.immediateMemoryTrial1Response,
        immediateMemoryTrial2Response:
          immediateMemory?.immediateMemoryTrial2Response,
        immediateMemoryTrial3Response:
          immediateMemory?.immediateMemoryTrial3Response,
        immediateMemoryClinicalNotes:
          immediateMemory?.immediateMemoryClinicalNotes,
        timeImmediateMemoryCompleted:
          immediateMemory?.timeImmediateMemoryCompleted,
        skipModuleFlag: immediateMemory?.skipModuleFlag,
        skipModuleComments: immediateMemory?.skipModuleComments,
        WORD_LIST_SIZE: 10,
        TOTAL_WORD_COUNT: 30,
        NORMATIVE_SCORE: 16,
      },
    });
  };

  const setNotesComments = (key: string, value: string) => {
    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      key,
      value,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const noAnswersCorrectResponse = (
    notTestedKey: string,
    notTestedValue: boolean,
    selectionKey?: string,
  ): void => {
    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      selectionKey || '',
      { noAnswersCorrect: notTestedValue },
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const setTrial1Response = (value: string): void => {
    const valueExists: boolean = !!(
      immediateMemory?.immediateMemoryTrial1Response &&
      value in immediateMemory?.immediateMemoryTrial1Response
    );

    // For the key/value pairs
    let fullResponse: any = {};
    // For the string array of values
    let wordsRecalledResponse: string[] = [];

    if (value) {
      if (immediateMemory?.immediateMemoryTrial1Response?.wordsRecalled) {
        wordsRecalledResponse = [
          ...immediateMemory?.immediateMemoryTrial1Response?.wordsRecalled,
          value,
        ];
        if (valueExists) {
          wordsRecalledResponse = wordsRecalledResponse.filter(
            (v: string): boolean => v !== value,
          );
        }
      } else {
        wordsRecalledResponse = [value];
      }

      fullResponse = {
        ...immediateMemory?.immediateMemoryTrial1Response,
        noAnswersCorrect: false,
        wordsRecalled: wordsRecalledResponse,
        [value]: value,
      };
    }
    if (value && valueExists) {
      delete fullResponse[value];
    }

    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'immediateMemoryTrial1Response',
      fullResponse,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const setTrial2Response = (value: string): void => {
    const valueExists: boolean = !!(
      immediateMemory?.immediateMemoryTrial2Response &&
      value in immediateMemory?.immediateMemoryTrial2Response
    );

    // For the key/value pairs
    let fullResponse: any = {};
    // For the string array of values
    let wordsRecalledResponse: string[] = [];

    if (value) {
      if (immediateMemory?.immediateMemoryTrial2Response?.wordsRecalled) {
        wordsRecalledResponse = [
          ...immediateMemory?.immediateMemoryTrial2Response?.wordsRecalled,
          value,
        ];
        if (valueExists) {
          wordsRecalledResponse = wordsRecalledResponse.filter(
            (v: string): boolean => v !== value,
          );
        }
      } else {
        wordsRecalledResponse = [value];
      }

      fullResponse = {
        ...immediateMemory?.immediateMemoryTrial2Response,
        noAnswersCorrect: false,
        wordsRecalled: wordsRecalledResponse,
        [value]: value,
      };
    }
    if (value && valueExists) {
      delete fullResponse[value];
    }

    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'immediateMemoryTrial2Response',
      fullResponse,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const setTrial3Response = (value: string): void => {
    const valueExists: boolean = !!(
      immediateMemory?.immediateMemoryTrial3Response &&
      value in immediateMemory?.immediateMemoryTrial3Response
    );

    // For the key/value pairs
    let fullResponse: any = {};
    // For the string array of values
    let wordsRecalledResponse: string[] = [];

    if (value) {
      if (immediateMemory?.immediateMemoryTrial3Response?.wordsRecalled) {
        wordsRecalledResponse = [
          ...immediateMemory?.immediateMemoryTrial3Response?.wordsRecalled,
          value,
        ];
        if (valueExists) {
          wordsRecalledResponse = wordsRecalledResponse.filter(
            (v: string): boolean => v !== value,
          );
        }
      } else {
        wordsRecalledResponse = [value];
      }

      fullResponse = {
        ...immediateMemory?.immediateMemoryTrial3Response,
        noAnswersCorrect: false,
        wordsRecalled: wordsRecalledResponse,
        [value]: value,
      };
    }
    if (value && valueExists) {
      delete fullResponse[value];
    }

    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'immediateMemoryTrial3Response',
      fullResponse,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  useEffect(() => {
    if (immediateMemory?.timeImmediateMemoryCompleted) {
      return;
    }
    const interval: NodeJS.Timer = setInterval(() => {
      assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
        moduleName,
        'timeImmediateMemoryCompleted',
        new Date(),
      );
    }, 1000);
    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  });

  useEffect((): void => {
    saveModule();
    domainService.dispatchBackEnabled(true);
    domainService.dispatchNextEnabledAndValidate(
      immediateMemory,
      AssessmentModules.ImmediateMemory,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    immediateMemory?.skipModuleFlag,
    immediateMemory?.skipModuleComments,
    immediateMemory?.immediateMemoryTrial1Response,
    immediateMemory?.immediateMemoryTrial2Response,
    immediateMemory?.immediateMemoryTrial3Response,
  ]);
  return (
    <>
      <Typography variant="h6" fontWeight="bold">
        {t(heading)}
      </Typography>
      <Instructions instructions={instructions} />
      {t(responseList[0].label)}
      <ImmediateMemoryWordsList
        wordListKey="immediateMemoryTrial1Response"
        wordsPresented={immediateMemory?.wordsPresentedKeyVal}
        setResponse={setTrial1Response}
        onSaveNoAnswersCorrect={noAnswersCorrectResponse}
        data={immediateMemory?.immediateMemoryTrial1Response}
      />
      {t(responseList[1].label)}
      <ImmediateMemoryWordsList
        wordListKey="immediateMemoryTrial2Response"
        wordsPresented={immediateMemory?.wordsPresentedKeyVal}
        setResponse={setTrial2Response}
        onSaveNoAnswersCorrect={noAnswersCorrectResponse}
        data={immediateMemory?.immediateMemoryTrial2Response}
      />
      {t(responseList[2].label)}
      <ImmediateMemoryWordsList
        wordListKey="immediateMemoryTrial3Response"
        wordsPresented={immediateMemory?.wordsPresentedKeyVal}
        setResponse={setTrial3Response}
        onSaveNoAnswersCorrect={noAnswersCorrectResponse}
        data={immediateMemory?.immediateMemoryTrial3Response}
      />

      {note && (
        <Notes
          label={t(note.label)}
          keyValue={note.key}
          onSave={setNotesComments}
          data={immediateMemory?.immediateMemoryClinicalNotes || ''}
          fullWidth
        />
      )}
      <SkipModule
        moduleKeyValue={moduleName}
        skipModuleFlag={immediateMemory?.skipModuleFlag}
        skipModuleComments={immediateMemory?.skipModuleComments}
      />
    </>
  );
}
